import React, { useId } from "react";
import { Box, Button, Dialog, useModals } from "@gusarov-studio/rubik-ui";
import { useNavigate } from "react-router";
import { useCreatePromptMutation } from "../../prompts/api";
import { routesMap } from "../../../router/routesMap";
import {
  NewPromptForm,
  NewPromptSchema,
  defaultPromptMeta,
  type NewPrompt,
} from "../forms/NewPromptForm";

interface AddPromptDialogContainerProps {
  promptId?: string;
  meta?: NewPrompt["meta"];
  test_cases?: NewPrompt["test_cases"];
  parentId?: string;
  onSuccess?: (promptId: string) => void;
  onFailure?: (error: Error) => void;
}

const AddPromptDialogContainer: React.FC<AddPromptDialogContainerProps> = ({
  promptId,
  meta,
  test_cases,
  parentId = null,
  onSuccess,
}) => {
  "use memo";
  const modals = useModals();
  const navigate = useNavigate();

  const [createPrompt, { isLoading: isCreating }] = useCreatePromptMutation();

  const formId = useId();
  const formDefaults = NewPromptSchema.parse({
    id: promptId,
    parent_id: parentId,
    ...(meta ? { meta: { ...defaultPromptMeta, ...meta } } : {}),
    ...(test_cases ? { test_cases } : {}),
  });

  const closeDialog = () => {
    modals.close();
  };

  const attemptAddPrompt = async (prompt: NewPrompt) => {
    const { data: promptId } = await createPrompt({
      prompt,
    });

    // DataFast is injected only on production
    if (window.datafast) {
      window.datafast("resource_created", {
        resource_id: `${promptId}`,
      });
    }

    closeDialog();
    navigate(routesMap.prompts.url({ promptId: promptId! }));

    if (onSuccess) onSuccess(promptId!);
  };

  return (
    <Dialog.Root open={true}>
      <Dialog.Content width="400px">
        <Dialog.Decoration type="grid" />
        <Dialog.Close
          onClick={closeDialog}
          disabled={isCreating}
        />
        <Dialog.Header>
          <Dialog.Title>{meta ? "Save prompt" : "Add prompt"}</Dialog.Title>

          <Dialog.Description>Please give a name for a new prompt</Dialog.Description>
        </Dialog.Header>

        <Box padding="20px 24px 32px">
          <NewPromptForm
            id={formId}
            defaultValues={formDefaults}
            disabled={isCreating}
            onSubmit={attemptAddPrompt}
          />
        </Box>

        <Dialog.Footer>
          <Button
            appearance="outline"
            variant="secondary"
            size="40"
            onClick={closeDialog}
            disabled={isCreating}
          >
            Cancel
          </Button>

          <Button
            variant="accent"
            size="40"
            type="submit"
            disabled={isCreating}
            form={formId}
          >
            Submit
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export { AddPromptDialogContainer };
